import React from 'react';
import { Phone, Linkedin, Twitter, Youtube, ArrowRight } from 'lucide-react';
import Icon from '../../../components/AppIcon';


const SITE = 'https://www.calltify.com';

const footerLinks = {
  Product: [
    { label: 'Features', href: `${SITE}/landing-page#solutions` },
    { label: 'Pricing', href: `${SITE}/landing-page#pricing` },
    { label: 'Benefits', href: `${SITE}/landing-page#benefits` },
    { label: 'Industries', href: `${SITE}/landing-page#industries` },
  ],
  Company: [
    { label: 'Privacy Policy', href: `${SITE}/privacy-policy` },
    { label: 'Terms of Service', href: `${SITE}/terms-of-service` },
    { label: 'Contact', href: `${SITE}/contact` },
    { label: 'About Us', href: `${SITE}/about` },
  ],
};

const SignupFooter = () => {
  return (
    <footer className="px-6 lg:px-8 pt-16 pb-8" style={{ background: '#0F1D52' }}>
      <div className="mx-auto" style={{ maxWidth: '1280px' }}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">

          {/* Column 1 — Logo + Description + Social */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="overflow-hidden" style={{ width: '32px', height: '32px' }}>
                <img
                  src="/assets/images/Logo-Calltify-(semibold)_1.png"
                  alt="Calltify logo"
                  className="h-8 object-cover object-left"
                />
              </div>
              <span className="text-lg font-bold text-white">Calltify</span>
            </div>
            <p className="text-sm leading-relaxed mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
              24/7 AI voice support that delivers faster resolutions, happier customers, and effortless scalability.
            </p>
            <div className="flex items-center gap-3">
              {[
                { icon: Linkedin, label: 'LinkedIn', href: 'https://www.linkedin.com/company/calltify/' },
                { icon: Twitter, label: 'X', href: '#' },
                { icon: Youtube, label: 'YouTube', href: '#' },
              ]?.map(({ icon: Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-250"
                  style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(79,107,255,0.3)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                  aria-label={label}
                >
                  <Icon size={15} color="rgba(255,255,255,0.7)" />
                </a>
              ))}
            </div>
          </div>

          {/* Column 2 — Product */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">Product</h4>
            <ul className="space-y-3">
              {footerLinks?.Product?.map(({ label, href }) => (
                <li key={label}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm transition-colors duration-200"
                    style={{ color: 'rgba(255,255,255,0.55)' }}
                    onMouseEnter={e => e.target.style.color = '#fff'}
                    onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.55)'}
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 — Company */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">Company</h4>
            <ul className="space-y-3">
              {footerLinks?.Company?.map(({ label, href }) => (
                <li key={label}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm transition-colors duration-200"
                    style={{ color: 'rgba(255,255,255,0.55)' }}
                    onMouseEnter={e => e.target.style.color = '#fff'}
                    onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.55)'}
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 — CTA Card */}
          <div>
            <div
              className="p-6 rounded-2xl"
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <h4 className="text-base font-bold text-white mb-2">Start your trial</h4>
              <p className="text-sm mb-1" style={{ color: 'rgba(255,255,255,0.6)' }}>Get ₹500 bonus credit.</p>
              <p className="text-xs mb-5" style={{ color: 'rgba(255,255,255,0.4)' }}>On Recharge of ₹10,000</p>
              <button
                onClick={() => { window.dataLayer?.push({ event: 'start_free_trial_click', location: 'footer' }); document.getElementById('auth-card')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all duration-250"
                style={{
                  background: 'linear-gradient(135deg, #4F6BFF 0%, #6E5CFF 100%)',
                  color: '#fff',
                  boxShadow: '0 4px 16px rgba(79,107,255,0.4)',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(79,107,255,0.5)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(79,107,255,0.4)'; }}
              >
                Start Trial
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
            © 2025 Calltify. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            {[
              { label: 'Privacy Policy', href: `${SITE}/privacy-policy` },
              { label: 'Terms of Service', href: `${SITE}/terms-of-service` },
            ]?.map(({ label, href }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs transition-colors duration-200"
                style={{ color: 'rgba(255,255,255,0.35)' }}
                onMouseEnter={e => e.target.style.color = 'rgba(255,255,255,0.7)'}
                onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.35)'}
              >
                {label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SignupFooter;
